import React from 'react';
import { Container, Grid, Typography, Box, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '../../../ThemeContext';

// Icons for capabilities
import VerifiedIcon from '@mui/icons-material/Verified';
import MovieIcon from '@mui/icons-material/Movie';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import SmartToyIcon from '@mui/icons-material/SmartToy';

// App Stores icons
import ShopIcon from '@mui/icons-material/Shop';
import AppleIcon from '@mui/icons-material/Apple';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';

const capabilities = [
  { icon: <VerifiedIcon />, title: 'Verified Profiles', desc: 'Gold & Silver badges' },
  { icon: <MovieIcon />, title: 'Audition Listings', desc: 'Verified opportunities' },
  { icon: <StorefrontIcon />, title: 'Marketplace', desc: 'Connect & collaborate' },
  { icon: <LocationOnIcon />, title: 'Shooting Locations', desc: 'Find perfect spots' },
  { icon: <LiveTvIcon />, title: 'Live Streaming', desc: 'Real-time engagement' },
  { icon: <SmartToyIcon />, title: 'AI Chatbot', desc: 'Ask Nemo' },
];

const CompanyOverview = () => {
  const { isDarkMode } = useTheme();

  const containerBg = isDarkMode ? 'linear-gradient(135deg, #0a0e1a 0%, #131b2f 100%)' : 'linear-gradient(135deg, #f0f7ff 0%, #e6f0fa 100%)';
  const cardBg = isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
  const textPrimary = isDarkMode ? '#ffffff' : '#0d1530';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';

  return (
    <Box sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 4, md: 6 }, px: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        <Box
          sx={{
            background: containerBg,
            borderRadius: '32px',
            overflow: 'hidden',
            border: `1px solid ${borderColor}`,
            boxShadow: isDarkMode ? '0 24px 48px rgba(0,0,0,0.3)' : '0 24px 48px rgba(7, 164, 245,0.08)',
            p: { xs: 3, md: 6, lg: 8 },
            position: 'relative'
          }}
        >
          <Grid container spacing={6} alignItems="center">
            
            {/* Left Content */}
            <Grid item xs={12} lg={7}>
              <motion.div initial={{ x: -40, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                
                <Chip
                  label="FOUNDED SEP 2023 · BENGALURU, INDIA"
                  size="small"
                  sx={{ background: isDarkMode ? 'rgba(7, 164, 245,0.2)' : '#e0f2fe', color: '#0ea5e9', fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.05em', mb: 3 }}
                />
                
                <Typography variant="h3" sx={{ fontWeight: 900, color: textPrimary, fontSize: { xs: '2.2rem', md: '3.2rem' }, mb: 3, lineHeight: 1.2 }}>
                  About Film-hook <Box component="span" sx={{ color: '#07a4f5' }}>Mediaapps</Box>
                </Typography>
                
                <Typography variant="body1" sx={{ color: textSecondary, lineHeight: 1.8, fontSize: '1.05rem', mb: 5, maxWidth: '90%' }}>
                  <strong style={{ color: textPrimary }}>Film-hook Mediaapps Private Limited</strong>, founded in September 2023 and headquartered in Bengaluru, Karnataka, is a forward-thinking company focused on delivering digital transformation solutions specifically for the film and entertainment industry. Its flagship platform, <strong style={{ color: '#07a4f5' }}>Filmhook</strong>, is a dynamic social network designed to connect filmmakers, actors, producers, directors, and creative professionals worldwide.
                </Typography>

                {/* Capabilities Grid */}
                <Grid container spacing={2} sx={{ mb: 5 }}>
                  {capabilities.map((cap, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: cardBg, border: `1px solid ${borderColor}`, p: 1.5, borderRadius: '12px' }}>
                        <Box sx={{ color: '#07a4f5', display: 'flex' }}>{cap.icon}</Box>
                        <Box>
                          <Typography sx={{ color: textPrimary, fontSize: '0.8rem', fontWeight: 800 }}>{cap.title}</Typography>
                          <Typography sx={{ color: textSecondary, fontSize: '0.7rem' }}>{cap.desc}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ height: '1px', background: borderColor, mb: 4 }} />

                {/* Footer Info */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 4 } }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: textSecondary, fontSize: '0.9rem' }}>Available on the</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>
                      <ShopIcon sx={{ color: '#10b981' }} /> Google Play Store
                    </Box>
                    <Typography sx={{ color: textSecondary, fontSize: '0.9rem', display: { xs: 'none', sm: 'block' } }}>|</Typography>
                    <Typography sx={{ color: textSecondary, fontSize: '0.9rem' }}>Coming soon on</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>
                      <AppleIcon sx={{ color: '#0ea5e9' }} /> App Store
                    </Box>
                  </Box>
                </Box>
                
                {/* Contact Pills */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: cardBg, border: `1px solid ${borderColor}`, px: 2, py: 1, borderRadius: '24px' }}>
                    <EmailIcon sx={{ color: '#07a4f5', fontSize: '1rem' }} />
                    <Typography sx={{ color: textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>support@filmhookapps.com</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: cardBg, border: `1px solid ${borderColor}`, px: 2, py: 1, borderRadius: '24px' }}>
                    <PhoneIcon sx={{ color: '#e11d48', fontSize: '1rem' }} />
                    <Typography sx={{ color: textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>+91 9629757656</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: cardBg, border: `1px solid ${borderColor}`, px: 2, py: 1, borderRadius: '24px' }}>
                    <LanguageIcon sx={{ color: '#0ea5e9', fontSize: '1rem' }} />
                    <Typography sx={{ color: textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>filmhookapps.com</Typography>
                  </Box>
                </Box>
                
              </motion.div>
            </Grid>

            {/* Right Image */}
            <Grid item xs={12} lg={5}>
              <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                <Box sx={{ position: 'relative', width: '100%', paddingBottom: '100%', borderRadius: '24px', overflow: 'hidden', boxShadow: isDarkMode ? '0 24px 64px rgba(0,0,0,0.6)' : '0 24px 64px rgba(7, 164, 245,0.15)' }}>
                  {/* High quality placeholder for the 3D cinematic scene */}
                  <Box component="img" src="https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&q=80" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default CompanyOverview;
